import React, { useState, useEffect } from 'react';

import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

import {
  Button,
  TextField,
  CircularProgress,
  useTheme,
  Grid,
} from '@mui/material';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../toasts/toastError';

import { getStyled } from './styles';
import toastSuccess from '../../toasts/toastSuccess';

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  number: Yup.string().min(8, 'Too Short!').max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email'),
});

export function ContactForm({ initialContact, onSave, onCancel }) {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [contact, setContact] = useState(initialContact);

  useEffect(() => {
    setContact(initialContact);
  }, [initialContact]);

  const handleSaveContact = async (values) => {
    try {
      if (contact.id) {
        await api.put(`/contacts/${contact.id}`, values);
      } else {
        const { data } = await api.post('/contacts', values);
        if (onSave) {
          onSave(data);
        }
      }
      toastSuccess(i18n.t('contactModal.success'), {
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'dark',
      });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Formik
      initialValues={contact}
      enableReinitialize={true}
      validationSchema={ContactSchema}
      onSubmit={(values, actions) => {
        try {
          handleSaveContact(values);
        } finally {
          actions.setSubmitting(false);
        }
      }}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <Field
                as={TextField}
                label={i18n.t('contactModal.form.name')}
                name='name'
                autoFocus
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                variant='outlined'
                margin='dense'
                style={theme.custom.textField}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Field
                as={TextField}
                label={i18n.t('contactModal.form.number')}
                name='number'
                error={touched.number && Boolean(errors.number)}
                helperText={touched.number && errors.number}
                placeholder='5544912344321'
                variant='outlined'
                margin='dense'
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Field
                as={TextField}
                label={i18n.t('contactModal.form.email')}
                name='email'
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                placeholder='Email'
                variant='outlined'
                margin='dense'
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Button
                    onClick={onCancel}
                    disabled={isSubmitting}
                    variant='outlined'
                    fullWidth
                    sx={theme.custom.cancelButton}
                  >
                    {i18n.t('contactModal.buttons.cancel')}
                  </Button>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Button
                    type='submit'
                    disabled={isSubmitting}
                    variant='outlined'
                    sx={theme.custom.confirmButton}
                    fullWidth
                  >
                    {contact.id
                      ? `${i18n.t('contactModal.buttons.okEdit')}`
                      : `${i18n.t('contactModal.buttons.okAdd')}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        style={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}
